import { Account } from './account';
import { ExchangeCode, MarketRegion, Permissions } from './enums/enums';
import ApplicationContext from './how/application-context';
import { CachedSecurity } from './quote/cached-security';

export const getMarketRegionByExchange = (exchange: string) => {
  switch (exchange) {
    // Stockholm
    case ExchangeCode.XSTO:
    case ExchangeCode.INDXSTO:
    // Copenhegan
    case ExchangeCode.XCSE:
    case ExchangeCode.INDXCSE:
    // Helsinki
    case ExchangeCode.XHEL:
    case ExchangeCode.INDXHEL:
      return MarketRegion.Nordic;
    // Canada
    case ExchangeCode.XTSE:
      return MarketRegion.TMX;
    // US
    case ExchangeCode.US:
      return MarketRegion.US;
    default:
      return MarketRegion.US;
  }
};

// For embedder region permission check
export const hasRegionAccess = (cachedData: CachedSecurity) => {
  const regionAccessFromConfig = ApplicationContext.configuration?.additionalData?.regionAccess;
  if (!regionAccessFromConfig) return false;

  const accessibleRegions = regionAccessFromConfig.split(',');
  return accessibleRegions.includes(cachedData.region);
};

// if AAPL is passed, allowed symbol to load only if US region access
// if AAPL.(US_EXCHANGE), allowed symbol to load only if US region access
// if AAPL.(CA_EXCHANGE), and if have US region access, deny to load respective symbol

// Get the region by account permissions
const getRegionByAccount = (account?: Account): string[] => {
  if (account && account.isUSAllowed && account.isCanadaAllowed) return [Permissions.US, Permissions.CA];
  if (account && account.isUSAllowed) return [Permissions.US];
  if (account && account.isCanadaAllowed) return [Permissions.CA];
  return null; // Default to US
};
// Find symbol with accessible exchange code based on region permissions
export const getSymbolWithExchangeCode = (cachedData: CachedSecurity[], account?: Account) => {
  const regionAccessFromConfig = getRegionByAccount(account);
  if (!regionAccessFromConfig) {
    return;
  }

  const cachedDataByRegion = cachedData.filter((c) => regionAccessFromConfig.includes(c.region));
  if (cachedDataByRegion.length === 0) return null;

  return cachedDataByRegion.find((c) => c.region === Permissions.US) || cachedDataByRegion[0];
};

export const splitSymbol = (symbol: string): [newSymbol: string, exchangeCode: string] => {
  const [newSymbol, exchangeCode] = symbol.toUpperCase()?.split(/\.(?=[^.]*$)/);
  return [newSymbol, exchangeCode];
};

export const getRegionBySymbol = (symbol: string, exchangeCode: string, cachedSecurity: CachedSecurity[]) => {
  if (!cachedSecurity) {
    return null;
  }
  return cachedSecurity.find((c) => c.symbol === symbol && c.exchangeCode === exchangeCode)?.region;
};

/**
 * !important
 * Used in the TradeTicket and ts-authenticate-widget (Tradestation)
 * to determine the region based on the exchange
 */

export const getRegionAndFormattedSymbol = (symbol: string) => {
  const [symbolWithoutExchange, exchange] = splitSymbol(symbol);

  // Determine region based on exchange
  const region = getMarketRegionByExchange(exchange);

  // If isEmbeddingPlatform and symbolDotExchange is true, return the symbol as it is
  if (
    ApplicationContext.configuration?.isEmbeddingPlatform &&
    ApplicationContext.configuration?.additionalData?.symbolDotExchange === 'true'
  ) {
    return { symbol, region, formattedSymbolByRegion: symbol };
  }

  // Format symbol based on region
  const formattedSymbolByRegion = region === MarketRegion.TMX ? symbol : symbolWithoutExchange;

  return { symbol, region, formattedSymbolByRegion, exchange, symbolWithoutExchange };
};

export const isSymbolExistsInCachedSecurities = (symbol: string, cachedSecurityData: CachedSecurity[]) => {
  if (!cachedSecurityData) {
    return false;
  }
  const [newSymbol, exchangeCode] = splitSymbol(symbol);
  const symbolInCached = cachedSecurityData.filter((cached) => cached.symbol === newSymbol);
  if (symbolInCached.length === 0) {
    return false;
  }
  if (exchangeCode && symbolInCached.length > 0) {
    return symbolInCached.some((s) => s.exchangeCode === exchangeCode);
  }
  return true;
};
